import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { get_access_authorities } from '../api/Access';
import { normalize_roles } from '../../helpers/hasRole';

const read_normalized_value = (value) => {
    return String(value || '').trim().toLowerCase();
};

const read_department_key = (value) => {
    return read_normalized_value(value).replace(/\s+(department|dep)$/i, '');
};

const read_has_token = (token) => {
    return String(token || '').trim().length > 0;
};

const read_string_value = (value) => {
    if (typeof value !== 'string') {
        return '';
    }

    return value.trim();
};

const read_email_from_text = (value) => {
    const textValue = read_string_value(value);

    if (!textValue.includes('@')) {
        return '';
    }

    return textValue;
};

const read_email_value = (memberItem = {}) => {
    return read_email_from_text(memberItem?.email?.email)
        || read_email_from_text(memberItem?.email)
        || read_email_from_text(memberItem?.memberEmail)
        || read_email_from_text(memberItem?.username)
        || read_email_from_text(memberItem?.userName)
        || read_email_from_text(memberItem?.preferred_username);
};

const read_keycloak_sub_id = (memberItem = {}) => {
    return memberItem?.sub || memberItem?.keycloakSub || memberItem?.subId || memberItem?.userId || memberItem?.id || '';
};

const read_erised_mongo_id = (memberItem = {}) => {
    return memberItem?._id || memberItem?.erisedId || memberItem?.erisedMongoId || '';
};

const read_default_permissions = () => {
    return {
        hasFullAccess: false,
        canAccessInternalPages: false,
        canSeeHiddenMessages: false,
        canRunCriticalActions: false,
        canConfirmOfferPayments: false,
        canUsePaymentActions: true,
        canCreateOffers: false,
        canContinuePreofferRequests: false,
        hasAcceptedTeamMembership: false,
        isAcceptedMember: false,
        isCompanyHead: false,
        isDepartmentHead: false,
        isGrowthDepartmentMember: false,
        isGrowthDepartmentHead: false,
        isMarketingDepartmentMember: false,
        isMarketingDepartmentHead: false,
        isWebsiteDepartmentMember: false,
        isWebsiteDepartmentHead: false,
        isBoD: false,
        isTeamLeader: false,
        isSalesDepartmentMember: false,
        isSalesTeamLeader: false,
        isMarketingTeamLeader: false,
        isWebsiteTeamLeader: false
    };
};

const read_has_full_access = (authRoles = []) => {
    return normalize_roles(authRoles).some((role) => ['King', 'Admin'].includes(role));
};

const read_full_access_permissions = () => {
    return Object.keys(read_default_permissions()).reduce((permissions, permissionKey) => {
        permissions[permissionKey] = true;
        return permissions;
    }, {});
};

const read_loading_permissions = () => {
    return {
        ...read_default_permissions(),
        canUsePaymentActions: false
    };
};

const read_fallback_permissions = () => {
    return {
        ...read_default_permissions(),
        canUsePaymentActions: true
    };
};

const read_department_rows = (companyExport = {}) => {
    return Array.isArray(companyExport?.departments) ? companyExport.departments : [];
};

const read_role_rows = (companyExport = {}) => {
    return Array.isArray(companyExport?.roles) ? companyExport.roles : [];
};

const read_matching_department = (companyExport = {}, departmentName = '') => {
    const normalizedDepartment = read_department_key(departmentName);

    return read_department_rows(companyExport).find((departmentItem) => {
        return read_department_key(departmentItem?.name) === normalizedDepartment;
    }) || {};
};

const read_is_company_head = (memberItem = {}, companyExport = {}) => {
    const normalizedMemberDepartment = read_department_key(memberItem?.department);
    const normalizedCompanyHeadDepartment = read_department_key(companyExport?.company_head_department);
    const departmentItem = read_matching_department(companyExport, memberItem?.department);

    if (!normalizedMemberDepartment.length) {
        return false;
    }

    return normalizedMemberDepartment === normalizedCompanyHeadDepartment
        || departmentItem?.company_head === true;
};

const read_is_department_head = (memberItem = {}, companyExport = {}) => {
    const normalizedMemberDepartment = read_department_key(memberItem?.department);
    const normalizedMemberRole = read_normalized_value(memberItem?.role);
    const departmentItem = read_matching_department(companyExport, memberItem?.department);
    const normalizedDepartmentHeadRole = read_normalized_value(departmentItem?.department_head_role);

    if (!normalizedMemberDepartment.length || !normalizedMemberRole.length) {
        return false;
    }

    if (normalizedDepartmentHeadRole.length && normalizedMemberRole === normalizedDepartmentHeadRole) {
        return true;
    }

    return read_role_rows(companyExport).some((roleRow) => {
        return read_department_key(roleRow?.department) === normalizedMemberDepartment
            && read_normalized_value(roleRow?.role) === normalizedMemberRole
            && roleRow?.department_head === true;
    });
};

const member_is_in_department = (memberItem = {}, departmentName = '') => {
    const memberDepartment = read_department_key(memberItem?.department);
    const requestedDepartment = read_department_key(departmentName);

    return memberDepartment === requestedDepartment;
};

const read_is_growth_department = (memberItem = {}) => {
    return member_is_in_department(memberItem, 'Growth')
        || member_is_in_department(memberItem, 'Sales');
};

const read_is_marketing_department = (memberItem = {}) => {
    return member_is_in_department(memberItem, 'Marketing');
};

const read_is_website_department = (memberItem = {}) => {
    return member_is_in_department(memberItem, 'Website')
        || member_is_in_department(memberItem, 'Webapp');
};

const read_member_name = (memberItem = {}) => {
    const firstName = memberItem?.firstname || memberItem?.firstName || '';
    const lastName = memberItem?.lastname || memberItem?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return memberItem?.name || memberItem?.fullName || fullName;
};

const read_member_roles = (memberItem = {}) => {
    return [
        memberItem?.role,
        memberItem?.company_head === true || memberItem?.isCompanyHead === true ? 'company_head' : '',
        memberItem?.department_head === true || memberItem?.isDepartmentHead === true ? 'department_head' : ''
    ].filter(Boolean);
};

const read_normalized_member = (memberItem = {}, companyExport = {}) => {
    const subId = read_keycloak_sub_id(memberItem);
    const memberWithRole = {
        ...memberItem,
        role: memberItem?.role || memberItem?.companyRole || ''
    };
    const isCompanyHead = read_is_company_head(memberWithRole, companyExport);
    const isDepartmentHead = read_is_department_head(memberWithRole, companyExport);

    return {
        id: subId,
        userId: subId,
        subId,
        firstname: memberItem?.firstname || memberItem?.firstName || '',
        lastname: memberItem?.lastname || memberItem?.lastName || '',
        name: read_member_name(memberItem),
        email: read_email_value(memberItem),
        username: memberItem?.username || memberItem?.userName || memberItem?.preferred_username || '',
        department: memberItem?.department || '',
        role: memberWithRole.role,
        authRoles: normalize_roles(memberItem?.authRoles || memberItem?.roles || []),
        accessStatus: memberItem?.accessStatus || 'accepted',
        company_head: isCompanyHead,
        department_head: isDepartmentHead,
        isCompanyHead,
        isDepartmentHead,
        isGrowthDepartmentMember: read_is_growth_department(memberItem),
        isMarketingDepartmentMember: read_is_marketing_department(memberItem),
        isWebsiteDepartmentMember: read_is_website_department(memberItem)
    };
};

const read_member_with_permissions = (memberItem = {}) => {
    const memberPermissions = read_member_permissions(memberItem);

    return {
        ...memberItem,
        memberRoles: read_member_roles(memberItem),
        isGrowthDepartmentHead: memberPermissions.isGrowthDepartmentHead,
        isMarketingDepartmentHead: memberPermissions.isMarketingDepartmentHead,
        isWebsiteDepartmentHead: memberPermissions.isWebsiteDepartmentHead,
        isSalesDepartmentMember: memberPermissions.isSalesDepartmentMember,
        isSalesTeamLeader: memberPermissions.isSalesTeamLeader,
        isMarketingTeamLeader: memberPermissions.isMarketingTeamLeader,
        isWebsiteTeamLeader: memberPermissions.isWebsiteTeamLeader
    };
};

const read_users_collection = (authoritiesData = {}) => {
    if (Array.isArray(authoritiesData?.users)) {
        return authoritiesData.users;
    }

    if (Array.isArray(authoritiesData?.members?.all)) {
        return authoritiesData.members.all;
    }

    return [];
};

export const read_matching_member = (members = [], subId = '', email = '', username = '') => {
    const normalizedSubId = read_normalized_value(subId);
    const identityAliases = [email, username]
        .map(read_normalized_value)
        .filter(Boolean);

    return members.find((memberItem) => {
        const memberSubId = read_normalized_value(read_keycloak_sub_id(memberItem));
        const memberAliases = [read_email_value(memberItem), memberItem?.username, memberItem?.preferred_username]
            .map(read_normalized_value)
            .filter(Boolean);

        return Boolean(
            normalizedSubId && memberSubId === normalizedSubId
        ) || Boolean(
            identityAliases.some((identityAlias) => memberAliases.includes(identityAlias))
        );
    }) || null;
};

export const read_member_permissions = (memberItem = null, authRoles = []) => {
    const hasAcceptedTeamMembership = read_normalized_value(memberItem?.accessStatus) === 'accepted';

    if (read_has_full_access(authRoles)) {
        return {
            ...read_full_access_permissions(),
            hasAcceptedTeamMembership
        };
    }

    const permissions = read_default_permissions();

    if (!memberItem) {
        return permissions;
    }

    const isAcceptedMember = read_normalized_value(memberItem.accessStatus) === 'accepted';
    const isCompanyHead = memberItem?.isCompanyHead === true || memberItem?.company_head === true;
    const isDepartmentHead = memberItem?.isDepartmentHead === true || memberItem?.department_head === true;
    const isGrowthDepartmentMember = memberItem?.isGrowthDepartmentMember === true || read_is_growth_department(memberItem);
    const isMarketingDepartmentMember = memberItem?.isMarketingDepartmentMember === true || read_is_marketing_department(memberItem);
    const isWebsiteDepartmentMember = memberItem?.isWebsiteDepartmentMember === true || read_is_website_department(memberItem);
    const isGrowthDepartmentHead = isDepartmentHead && isGrowthDepartmentMember;
    const isMarketingDepartmentHead = isDepartmentHead && isMarketingDepartmentMember;
    const isWebsiteDepartmentHead = isDepartmentHead && isWebsiteDepartmentMember;
    const canCreateOffers = isAcceptedMember && isGrowthDepartmentMember;
    const canAccessInternalPages = isAcceptedMember;
    const canConfirmOfferPayments = isAcceptedMember && (isCompanyHead || isGrowthDepartmentHead);
    const canRunCriticalActions = canConfirmOfferPayments;

    return {
        canAccessInternalPages,
        canSeeHiddenMessages: canAccessInternalPages,
        canRunCriticalActions,
        canConfirmOfferPayments,
        canUsePaymentActions: !canAccessInternalPages,
        canCreateOffers,
        canContinuePreofferRequests: isAcceptedMember && (isCompanyHead || isDepartmentHead),
        hasAcceptedTeamMembership,
        isAcceptedMember,
        isCompanyHead,
        isDepartmentHead,
        isGrowthDepartmentMember,
        isGrowthDepartmentHead,
        isMarketingDepartmentMember,
        isMarketingDepartmentHead,
        isWebsiteDepartmentMember,
        isWebsiteDepartmentHead,
        isBoD: isCompanyHead,
        isTeamLeader: isDepartmentHead,
        isSalesDepartmentMember: isGrowthDepartmentMember,
        isSalesTeamLeader: isGrowthDepartmentHead,
        isMarketingTeamLeader: isMarketingDepartmentHead,
        isWebsiteTeamLeader: isWebsiteDepartmentHead
    };
};

const read_offer_member = (hrMember = null, erisedUser = {}, hasFullAccess = false) => {
    if (!hrMember && !hasFullAccess) {
        return null;
    }

    const memberSource = hrMember || erisedUser;
    const subId = read_keycloak_sub_id(erisedUser) || read_keycloak_sub_id(memberSource);

    return {
        _id: read_erised_mongo_id(erisedUser),
        id: subId,
        userId: subId,
        subId,
        email: read_email_value(erisedUser) || read_email_value(memberSource),
        firstname: memberSource?.firstname || memberSource?.firstName || '',
        lastname: memberSource?.lastname || memberSource?.lastName || '',
        name: read_member_name(memberSource),
        username: memberSource?.username || memberSource?.preferred_username || '',
        department: memberSource?.department || '',
        role: memberSource?.role || '',
        accessStatus: hasFullAccess ? 'accepted' : (memberSource?.accessStatus || 'accepted')
    };
};

export const HrAccessContext = createContext({
    isReady: false,
    isLoading: false,
    errorMessage: '',
    member: null,
    members: [],
    users: [],
    companyExport: {},
    roleSettings: {},
    permissions: read_default_permissions()
});

export const useHrAccess = () => {
    return useContext(HrAccessContext);
};

export const use_hr_access = useHrAccess;

export const HrAccessProvider = ({ children }) => {
    const user = useSelector((state) => state.user.user);
    const token = useSelector((state) => state.user.token);
    const currentUserSubId = read_keycloak_sub_id(user || {});
    const currentUserEmail = read_email_value(user || {});
    const currentUserUsername = user?.username || user?.userName || user?.preferred_username || '';
    const currentUserRolesKey = normalize_roles(user?.roles || []).sort().join('|');
    const [isReady, setIsReady] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [member, setMember] = useState(null);
    const [members, setMembers] = useState([]);
    const [companyExport, setCompanyExport] = useState({});
    const [permissions, setPermissions] = useState(read_default_permissions());

    useEffect(() => {
        let mounted = true;
        const hasAuthenticatedSession = Boolean(user && read_has_token(token));

        if (!hasAuthenticatedSession) {
            setIsReady(true);
            setIsLoading(false);
            setErrorMessage('');
            setMember(null);
            setMembers([]);
            setCompanyExport({});
            setPermissions(read_default_permissions());

            return () => {
                mounted = false;
            };
        }

        const load_hr_access = async() => {
            setIsReady(false);
            setIsLoading(true);
            setErrorMessage('');
            setMember(null);
            setMembers([]);
            setCompanyExport({});
            setPermissions(read_loading_permissions());

            try {
                const response = await get_access_authorities({
                    includeMembers: true
                });

                if (!mounted) {
                    return;
                }

                const authoritiesData = response?.data?.data || {};
                const nextCompanyExport = authoritiesData?.companyExport || {};
                const normalizedMembers = read_users_collection(authoritiesData).map((memberItem) => {
                    return read_normalized_member(memberItem, nextCompanyExport);
                });
                const membersWithPermissions = normalizedMembers.map((memberItem) => {
                    return read_member_with_permissions(memberItem);
                });
                const matchedHrMember = read_matching_member(
                    membersWithPermissions,
                    currentUserSubId,
                    currentUserEmail,
                    currentUserUsername
                );
                const nextPermissions = read_member_permissions(matchedHrMember, user?.roles || []);

                setMember(read_offer_member(matchedHrMember, user, nextPermissions.hasFullAccess));
                setMembers(membersWithPermissions);
                setCompanyExport(nextCompanyExport);
                setPermissions(nextPermissions);
                setIsReady(true);
            } catch (error) {
                if (!mounted) {
                    return;
                }

                const fallbackPermissions = read_member_permissions(null, user?.roles || []);

                setErrorMessage(fallbackPermissions.hasFullAccess ? '' : 'Could not load HR access.');
                setMember(read_offer_member(null, user, fallbackPermissions.hasFullAccess));
                setMembers([]);
                setCompanyExport({});
                setPermissions(fallbackPermissions.hasFullAccess ? fallbackPermissions : read_fallback_permissions());
                setIsReady(true);
            } finally {
                if (mounted) {
                    setIsLoading(false);
                }
            }
        };

        load_hr_access();

        return () => {
            mounted = false;
        };
    }, [currentUserSubId, currentUserEmail, currentUserUsername, currentUserRolesKey, token]);

    return (
        <HrAccessContext.Provider value={{
            isReady,
            isLoading,
            errorMessage,
            member,
            members,
            users: members,
            companyExport,
            roleSettings: {},
            permissions
        }}>
            {children}
        </HrAccessContext.Provider>
    );
};
